import React, { useEffect, useState } from 'react';
import { X, User, DollarSign, Calendar, Clock } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';

const VITE_API_URL = import.meta.env.VITE_API_URL;

const PartnerDetailsModal = ({ isOpen, onClose, partnerId }) => {
    const { token } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('referrals'); // 'referrals' | 'history'

    useEffect(() => {
        if (isOpen && partnerId) {
            setLoading(true);
            fetch(`${VITE_API_URL}/admin/referrers/${partnerId}/details`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
            .then(res => res.json())
            .then(data => {
                setData(data);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
        } else {
            setData(null);
        }
    }, [isOpen, partnerId, token]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-gray-800 rounded-xl w-full max-w-4xl border border-gray-700 shadow-2xl h-[90vh] flex flex-col">
                
                {/* Header / Loading State */}
                <div className="flex justify-between items-center p-6 border-b border-gray-700 bg-gray-900/50">
                    <div>
                        <h2 className="text-xl font-bold text-white">Partner Activity Log</h2>
                        {data?.partner && <p className="text-gray-400 text-sm">ID: {data.partner.ref_code}</p>}
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-full text-gray-400 hover:text-white transition">
                        <X size={24} />
                    </button>
                </div>

                {loading ? (
                    <div className="flex-1 flex items-center justify-center text-gray-400">Loading details...</div>
                ) : data ? (
                    <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
                        
                        {/* --- LEFT SIDE: PROFILE SUMMARY --- */}
                        <div className="w-full md:w-1/3 bg-gray-900/30 border-r border-gray-700 p-6 overflow-y-auto">
                            <div className="text-center mb-6">
                                <div className="w-20 h-20 bg-blue-900/50 text-blue-400 rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-3 border border-blue-800">
                                    {data.partner.full_name.charAt(0)}
                                </div>
                                <h3 className="text-lg font-bold text-white">{data.partner.full_name}</h3>
                                <p className="text-sm text-gray-500">{data.partner.email}</p>
                                <p className="text-sm text-gray-500">{data.partner.phone}</p>
                            </div>

                            <div className="space-y-4">
                                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                                    <div className="text-xs text-gray-500 uppercase font-bold mb-1">Wallet Balance</div>
                                    <div className="text-2xl font-bold text-green-400">৳ {data.partner.wallet_balance}</div>
                                </div>
                                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                                    <div className="text-xs text-gray-500 uppercase font-bold mb-1">Lifetime Earnings</div>
                                    <div className="text-xl font-bold text-white">৳ {data.partner.total_earned}</div>
                                </div>
                                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                                    <div className="text-xs text-gray-500 uppercase font-bold mb-1">Joined Date</div>
                                    <div className="text-sm text-gray-300">
                                        {new Date(data.partner.created_at).toLocaleDateString()}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* --- RIGHT SIDE: TABS & DATA --- */}
                        <div className="flex-1 flex flex-col bg-gray-800">
                            {/* Tabs */}
                            <div className="flex border-b border-gray-700">
                                <button 
                                    onClick={() => setActiveTab('referrals')}
                                    className={`flex-1 py-4 text-sm font-bold text-center transition ${activeTab === 'referrals' ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-700'}`}
                                >
                                    Referred Doctors ({data.referrals.length})
                                </button>
                                <button 
                                    onClick={() => setActiveTab('history')}
                                    className={`flex-1 py-4 text-sm font-bold text-center transition ${activeTab === 'history' ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-700'}`}
                                >
                                    Transaction History
                                </button>
                            </div>

                            {/* Content Area */}
                            <div className="flex-1 overflow-y-auto p-0">
                                
                                {/* TAB 1: REFERRALS LIST */}
                                {activeTab === 'referrals' && (
                                    <table className="w-full text-left text-sm text-gray-300">
                                        <thead className="bg-gray-900/50 text-xs uppercase font-bold text-gray-500 sticky top-0">
                                            <tr>
                                                <th className="p-4">Doctor Name</th>
                                                <th className="p-4">Joined</th>
                                                <th className="p-4">Sub. Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-700">
                                            {data.referrals.map((doc, i) => (
                                                <tr key={i} className="hover:bg-gray-700/30">
                                                    <td className="p-4">
                                                        <div className="font-bold text-white">{doc.full_name}</div>
                                                        <div className="text-xs text-gray-500">{doc.email}</div>
                                                    </td>
                                                    <td className="p-4 text-gray-400">{new Date(doc.created_at).toLocaleDateString()}</td>
                                                    <td className="p-4">
                                                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                                            doc.subscription_status === 'active' ? 'bg-green-900 text-green-300' : 
                                                            doc.subscription_status === 'trial' ? 'bg-blue-900 text-blue-300' : 'bg-red-900 text-red-300'
                                                        }`}>
                                                            {doc.subscription_status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                            {data.referrals.length === 0 && (
                                                <tr><td colSpan="3" className="p-8 text-center text-gray-500">No referrals yet.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}

                                {/* TAB 2: TRANSACTION HISTORY */}
                                {activeTab === 'history' && (
                                    <div className="p-4 space-y-3">
                                        {data.history.map((tx, i) => (
                                            <div key={i} className={`p-4 rounded-lg border flex justify-between items-center ${
                                                tx.type === 'commission' ? 'bg-gray-700/30 border-gray-600' : 'bg-gray-900/50 border-gray-700'
                                            }`}>
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-2 rounded-full ${tx.type === 'commission' ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'}`}>
                                                        {tx.type === 'commission' ? <DollarSign size={18} /> : <Clock size={18} />}
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-white capitalize">{tx.type}</div>
                                                        <div className="text-xs text-gray-500">{new Date(tx.created_at).toLocaleString()}</div>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className={`font-bold text-lg ${tx.type === 'commission' ? 'text-green-400' : 'text-red-400'}`}>
                                                        {tx.type === 'commission' ? '+' : '-'} ৳{tx.amount}
                                                    </div>
                                                    <div className="text-xs text-gray-500 uppercase font-bold">{tx.status}</div>
                                                </div>
                                            </div>
                                        ))}
                                        {data.history.length === 0 && (
                                            <div className="text-center text-gray-500 py-10">No transaction history found.</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-red-400">Failed to load data.</div>
                )}
            </div>
        </div>
    );
};

export default PartnerDetailsModal;